/** @jsx jsx */
import { css, jsx } from '@emotion/react';

import * as React from 'react';

import PropTypes from 'prop-types';
import type { LegacyContextType } from 'types/legacy-context-types';

import user from 'js/lib/user';

import { Button, Typography, Typography2 } from '@coursera/cds-core';

import type { ThirdPartyAuthService } from 'bundles/authentication-common/constants';
import DegreesEmbeddedAuthContent from 'bundles/expression-of-interest/components/xddp/DegreesEmbeddedAuthContent';
import type { Maybe } from 'bundles/xddp/__generated__/baseContentfulTypes';
import EoiContainer from 'bundles/xddp/components/landing/EoiContainer';
import LoadingIcon from 'bundles/xddp/components/shared/LoadingIcon';

import _t from 'i18n!nls/xddp';

import 'css!bundles/xddp/components/__styles__/shared/EmbeddedRegForm';

const styles = {
  loadingIconContainer: css`
    width: 100%;
    display: flex;
    align-items: center;
    justify-content: center;
    padding-top: 24px;
  `,
};

export type Props = {
  onGoBackButtonClick?: () => void;
  isNavFixed?: boolean;
  shortName?: Maybe<string>;
  partnerName?: Maybe<string>;
  slug: string;
  extraFormContent?: React.ReactElement;
  customValidationFunc?: () => boolean;
  formToggled?: () => void;
  surveyQuestionsLoading?: boolean;
  onRegSuccess?: () => void;
};

type TrackingProps = { trackingProductId: string };

type EOIText = {
  signupTitle: string;
  loginTitle: string;
  signupSubText: string | JSX.Element;
  loginSubText: string | JSX.Element;
};

const THIRD_PARTY_AUTH_SERVICES_ENABLED: Array<ThirdPartyAuthService> = ['google', 'facebook'];

export const getTranslations = (shortName?: Maybe<string>): EOIText => ({
  signupTitle: _t('Interested to learn more?'),
  loginTitle: _t('Interested to learn more?'),
  loginSubText: _t('Sign in with your Coursera account to learn more about the #{shortName} degree.', {
    shortName,
  }),
  signupSubText: _t(
    'Create an account to receive info session dates and application deadlines about this degree and other educational content on Coursera.'
  ),
});

class EmbeddedRegForm extends React.Component<Props & TrackingProps> {
  static contextTypes = {
    router: PropTypes.object.isRequired,
  };

  declare context: LegacyContextType<typeof EmbeddedRegForm.contextTypes>;

  componentDidMount = () => {
    const { isNavFixed } = this.props;
    const embeddedRegFormEl = document.querySelector('.rc-EmbeddedRegForm');

    if (embeddedRegFormEl && isNavFixed) {
      embeddedRegFormEl.scrollIntoView();
    }
  };

  handleUserSignedUp = () => {
    this.props.onRegSuccess?.();
    window.location.reload();
  };

  renderEoiForm = () => {
    const { slug, partnerName, shortName } = this.props;

    return (
      <div
        className="rc-EmbeddedRegForm section-embedded"
        css={css`
          margin-bottom: 32px;
          padding: 24px;
        `}
      >
        <EoiContainer slug={slug} shortName={shortName} partnerName={partnerName} prepopulateUserInfo={true} />
      </div>
    );
  };

  renderAuthForm = () => {
    const { onGoBackButtonClick, shortName, trackingProductId } = this.props;
    const translations = getTranslations(shortName);

    const { signupTitle, signupSubText } = translations;

    const title = signupTitle;
    const subText = signupSubText;

    return (
      <div
        className="rc-EmbeddedRegForm section-embedded"
        css={css`
          margin-bottom: 32px;
          padding: 24px;
        `}
      >
        {/* eslint-disable-next-line @coursera-web/custom-rules/prefer-cds-typography2 -- FIXME: legacy; migrate to Typography2 */}
        <Typography className="eoi-header" data-e2e="eoi-header-title" variant="h2">
          {title}
        </Typography>
        <Typography2 component="p" data-e2e="eoi-subtitle" variant="bodySecondary">
          {subText}
          {onGoBackButtonClick && (
            <Button variant="ghost" size="small" className="eoi-link" onClick={onGoBackButtonClick}>
              {_t('Or go back. ')}
            </Button>
          )}
        </Typography2>
        {this.props.surveyQuestionsLoading ? (
          <div css={styles.loadingIconContainer}>
            <LoadingIcon color="interactive" />
          </div>
        ) : (
          <DegreesEmbeddedAuthContent
            onUserRegistered={this.handleUserSignedUp}
            services={THIRD_PARTY_AUTH_SERVICES_ENABLED}
            formToggled={this.props.formToggled}
            extraFormContent={this.props.extraFormContent}
            trackingProductId={trackingProductId}
          />
        )}
      </div>
    );
  };

  render() {
    return user.isAuthenticatedUser() ? this.renderEoiForm() : this.renderAuthForm();
  }
}

export default EmbeddedRegForm;
